import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { formatTime } from '../../utils/vttParser';
import './ChapterNavigation.css';

/**
 * Finds the index of the currently active chapter based on currentTime.
 *
 * @param {Array} chapters - List of chapter objects
 * @param {number} currentTime - Current playback time in seconds
 * @returns {number} Index of active chapter, or -1 if none
 */
export const findActiveChapterIndex = (chapters, currentTime) => {
  if (!chapters || chapters.length === 0 || typeof currentTime !== 'number' || currentTime < 0) {
    return -1;
  }

  for (let i = 0; i < chapters.length; i++) {
    const current = chapters[i];
    const next = chapters[i + 1];
    const endTime = current.endTime !== undefined && current.endTime !== null ? current.endTime : next?.startTime;

    if (endTime !== undefined && endTime !== null) {
      if (currentTime >= current.startTime && currentTime < endTime) {
        return i;
      }
    } else if (currentTime >= current.startTime) {
      return i;
    }
  }

  // If playback reached or passed the start time of the last chapter
  if (chapters.length > 0 && currentTime >= chapters[chapters.length - 1].startTime) {
    return chapters.length - 1;
  }

  return -1;
};

const ChapterNavigation = ({ chapters = [], currentTime = 0, onSeek, disabled = false }) => {
  const { t } = useTranslation();

  if (!chapters || chapters.length === 0) {
    return null;
  }

  const activeIndex = findActiveChapterIndex(chapters, currentTime);

  return (
    <nav className="chapter-navigation" aria-label={t('record_chapters_header') || 'Chapters'}>
      <h3 className="chapter-navigation-title">{t('record_chapters_header') || 'Chapters'}</h3>
      <ul className="chapter-list">
        {chapters.map((chapter, index) => {
          const isActive = index === activeIndex;
          const chapterKey = chapter.id ? `chapter-${chapter.id}` : `chapter-${index}`;

          return (
            <li key={chapterKey} className="chapter-item">
              <button
                type="button"
                className={`chapter-button ${isActive ? 'active' : ''}`}
                onClick={() => onSeek?.(chapter.startTime)}
                aria-current={isActive ? 'true' : undefined}
                disabled={disabled}
              >
                <span className="chapter-time">{formatTime(chapter.startTime)}</span>
                <span className="chapter-title">{chapter.text}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

ChapterNavigation.propTypes = {
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      startTime: PropTypes.number.isRequired,
      endTime: PropTypes.number,
      text: PropTypes.string.isRequired
    })
  ),
  currentTime: PropTypes.number,
  onSeek: PropTypes.func,
  disabled: PropTypes.bool
};

export default ChapterNavigation;
